import { ChallengeService } from '@duck/api-definitions'
import {
    CheckBadgeIcon,
    ChevronRightIcon,
} from '@heroicons/react/24/outline'
import {
    Badge,
    Progress,
} from '@duck/ui'
import { CompletedChallenge } from '../FeedbackDialog'
import { TranslationKey, useTar } from '@/modules'
import {
    challengeDifficultyDisplayMap,
    getChallengeDifficultyColorAccentVariant,
} from '@/utils/challengeData.utils'
import { formatWorkTime } from '@/utils/time.utils'
import { RelativeDateTime } from '../datetime/RelativeDateTime'
import { OrgAvatar } from '../OrgAvatar'
import { WeaponIcon } from '../WeaponIcon'
import { useOrgBranding } from '@/hooks/useOrgBranding'

const WEAPON_LABEL_KEY: Record<ChallengeService.TaskWeaponsOfChoiceEnum, TranslationKey> = {
    [ChallengeService.TaskWeaponsOfChoiceEnum.Sql]: 'common.sql',
    [ChallengeService.TaskWeaponsOfChoiceEnum.Python]: 'common.python',
}

interface Props {
    completedChallenge: CompletedChallenge
    attemptNumber: number
    totalAttempts: number
    isPublic: boolean
    onShowResults: (c: CompletedChallenge) => void
    onShowSubmission: (c: CompletedChallenge) => void
}

const PROCESSING_TOTAL = 15

const computeProcessingValue = (status?: ChallengeService.FeedbackStatusEnum): number => {
    if (!status || !status.startsWith('processing')) return 0
    const n = Number(status.replace('processing', ''))
    if (Number.isNaN(n)) return 0
    return Math.min(100, Math.max(0, (n / PROCESSING_TOTAL) * 100))
}

const getProcessingStageLabelKey = (status?: ChallengeService.FeedbackStatusEnum): TranslationKey => {
    const n = status?.startsWith('processing') ? Number(status.replace('processing', '')) : 0
    const stage = Number.isNaN(n) ? 1 : Math.min(PROCESSING_TOTAL, Math.max(1, n))
    return `challenges.processingStage${stage}` as TranslationKey
}

export const CompletedSubmissionCard = ({
    completedChallenge,
    attemptNumber,
    totalAttempts,
    isPublic,
    onShowResults,
    onShowSubmission,
}: Props) => {
    const { t } = useTar()
    const { challenge, task, icon } = completedChallenge
    const Icon = icon || CheckBadgeIcon

    const feedbackStatus = challenge.feedbackStatus

    const isAssignment = !!challenge.assignmentId
    const { branding } = useOrgBranding(challenge.organizationId)
    const usedWeapon = challenge.workState?.chosenWeapon
        ?? task.allowedWeapons?.[0]
        ?? ChallengeService.TaskWeaponsOfChoiceEnum.Sql
    const isFeedbackProcessed = feedbackStatus === ChallengeService.FeedbackStatusEnum.Processed
    const isFeedbackExposed = !isAssignment || challenge.feedbackReleaseOverride === true
    const canViewFeedback = isFeedbackProcessed && isFeedbackExposed
    // Only self-driven submissions actively mid-feedback are "processing". A
    // missing/failed status (undefined) or any non-"processing*" state must stay
    // interactive so the user can still open their submission, rather than being
    // frozen behind a progress bar that never completes.
    const isProcessing = !isAssignment && !!feedbackStatus?.startsWith('processing')

    const handleCardClick = () => {
        if (isProcessing) return
        if (canViewFeedback) {
            onShowResults(completedChallenge)
        } else {
            onShowSubmission(completedChallenge)
        }
    }

    const handleCardKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.target !== e.currentTarget) return
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            handleCardClick()
        }
    }

    const interactive = !isProcessing

    return (
        <div
            role={interactive ? 'button' : undefined}
            tabIndex={interactive ? 0 : -1}
            aria-label={interactive ? task.name : undefined}
            onClick={interactive ? handleCardClick : undefined}
            onKeyDown={interactive ? handleCardKeyDown : undefined}
            className={`group bg-card border border-border rounded-lg p-4 transition-colors
                ${interactive ? 'cursor-pointer hover:bg-accent/20 hover:border-border/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring' : ''}`}
        >
            <div className="flex items-start md:items-center gap-4">
                {isAssignment ? (
                    <OrgAvatar
                        org={{ name: branding?.name ?? '', logoSquareURL: branding?.logoSquareURL }}
                        className="w-11 h-11 flex-shrink-0"
                    />
                ) : (
                    <div className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0 bg-success/10">
                        <Icon className="w-5 h-5 text-success" />
                    </div>
                )}

                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                        <h3 className="text-base font-semibold text-foreground truncate">{task.name}</h3>
                        {!isAssignment && (
                            <span className={`px-2 py-0.5 rounded-full border text-[10px] font-medium ${getChallengeDifficultyColorAccentVariant(task.difficulty)}`}>
                                {t(challengeDifficultyDisplayMap[task.difficulty])}
                            </span>
                        )}
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-border text-[10px] text-muted-foreground bg-background">
                            <span className="text-muted-foreground/80">{t('challenges.attempt')}</span>
                            <span className="font-bold text-foreground">{attemptNumber}</span>
                            <span className="text-muted-foreground/60">/ {totalAttempts}</span>
                        </span>
                        {isPublic && (
                            <Badge variant="default" className="bg-primary/10 text-primary border-primary/30 text-[10px] font-semibold">
                                {t('share.toggle.public')}
                            </Badge>
                        )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full border uppercase tracking-wide bg-muted/10 text-muted-foreground border-muted/20">
                            <WeaponIcon weapon={usedWeapon} className="h-3.5 w-3.5 shrink-0" />
                            {t(WEAPON_LABEL_KEY[usedWeapon])}
                        </span>
                        <span>·</span>
                        <span className="font-semibold text-foreground">{formatWorkTime(challenge.workTime, t)}</span>
                        {challenge.endTime && (
                            <>
                                <span>·</span>
                                <RelativeDateTime
                                    isoDateString={challenge.endTime}
                                    makeLabel={(time) => t('userFeedback.submittedTIME', { time })}
                                />
                            </>
                        )}
                    </div>
                </div>

                <div className="hidden md:flex items-center gap-2 flex-shrink-0">
                    {isProcessing ? (
                        <div className="w-56">
                            <div className="text-xs text-foreground mb-1 truncate">
                                {t(getProcessingStageLabelKey(feedbackStatus))}
                            </div>
                            <Progress className="h-2" value={computeProcessingValue(feedbackStatus)} />
                        </div>
                    ) : (
                        <ChevronRightIcon className="w-4 h-4 text-muted-foreground/40 group-hover:text-muted-foreground transition-colors" />
                    )}
                </div>
            </div>

            {isProcessing && (
                <div className="md:hidden mt-3 pt-3 border-t border-border">
                    <div className="text-xs text-foreground mb-1 truncate">
                        {t(getProcessingStageLabelKey(feedbackStatus))}
                    </div>
                    <Progress className="h-2" value={computeProcessingValue(feedbackStatus)} />
                </div>
            )}
        </div>
    )
}
